/**
 * Feedback Plan Preview Modal (PR-1, read-only)
 *
 * Wraps `POST /reports/:id/feedback/plan` and shows the bucketed plan the
 * backend computed — counts per bucket, expandable per-target feedback
 * lists, and a recommended-action banner. Applying queues `PATCH /regenerate`
 * with `feedback_plan_id` (see `applyFeedbackPlan`).
 *
 * Mount points:
 *   - QA tab in `ReportViewer` (with score panel entry points).
 *   - Report Content tab: opened from the Regenerate V2 flow only (no separate toolbar button).
 *
 * The modal renders into a portal so it floats above the surrounding tab
 * UI cleanly.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Columns2,
  Info,
  Loader2,
  RefreshCw,
  X as XIcon,
  Zap,
} from 'lucide-react';
import { Button } from '../UI/Button';
import { Badge } from '../UI/Badge';
import {
  applyFeedbackPlan,
  buildFeedbackPlanPromptPreviewText,
  createFeedbackPlan,
  effectiveFeedbackPlanRecommendation,
  feedbackPlanHasV2PromptWork,
  FeedbackPlanApplyError,
  getV2Diff,
  type FeedbackPlanRecommendation,
  type FeedbackPlanResponse,
  type RegenerationJobStatus,
  type V2DiffResponse,
} from '../../api/feedbackApi';
import { scoreReadiness } from '../../api/qaApi';
import { getReportJobStatus, getReport } from '../../api/reports';
import type { ReportJobStatus } from '../../types/api';
import { useToastStore } from '../../stores/toast';
import { SelectionCorrectionsPanel } from './SelectionCorrectionsPanel';

function formatFeedbackTargetLabel(
  targetKey: string,
  roleTitles: Record<string, string>,
): string {
  const [kind, ...rest] = targetKey.split(':');
  const id = rest.join(':');
  if (kind === 'staff') {
    const title = roleTitles[id];
    if (title) return `${title} · ${id}`;
  }
  return id;
}

function sortFeedbackTargetEntries(
  entries: [string, { text: string; source_id?: string | null }[]][],
): typeof entries {
  return [...entries].sort(([, aItems], [, bItems]) => {
    const aCorr = aItems.some((it) => String(it.source_id ?? '').startsWith('correction:'));
    const bCorr = bItems.some((it) => String(it.source_id ?? '').startsWith('correction:'));
    if (aCorr && !bCorr) return -1;
    if (!aCorr && bCorr) return 1;
    return 0;
  });
}

export interface FeedbackPlanPreviewModalProps {
  reportId: string;
  open: boolean;
  onClose: () => void;
  /** Optional: callback fired once a plan has been computed. */
  onPlanComputed?: (plan: FeedbackPlanResponse) => void;
  /** Optional default for the "min severity" filter. */
  defaultMinSeverity?: 'minor' | 'major' | 'critical';
  /**
   * When the dialog opens, immediately POST /feedback/plan (Step 1 from the
   * score card shortcut).
   */
  autoComputeOnOpen?: boolean;
  /**
   * After open, expand the prompt preview accordion (Step 2 entry / approve path).
   */
  expandPromptPreviewOnOpen?: boolean;
  /**
   * When true (default), the modal polls `/reports/jobs/:id/status` and shows a
   * full-screen progress overlay instead of invoking `onPlanApplied` immediately.
   */
  embedJobProgressOverlay?: boolean;
  /**
   * Only used when `embedJobProgressOverlay` is false: fired as soon as apply
   * returns with a queued job (legacy delegation to host polling).
   */
  onPlanApplied?: (job: RegenerationJobStatus, planId: string) => void;
  /**
   * After embedded polling completes successfully — runs once the report query
   * has been invalidated and refetched (e.g. bump version dropdown).
   */
  onEmbeddedRegenerationComplete?: () => void | Promise<void>;
}

const RECOMMENDATION_LABEL: Record<FeedbackPlanRecommendation, string> = {
  v2_prompt_augmented: 'Refine existing proposal (V2 rewrite with reviewer tips)',
  section_regen: 'Re-research specific sections (new RAG evidence)',
  targeted_section_regen: 'Fill missing sections + rewrite proposal (~2 min)',
  v1v2_full_regen: 'Full pipeline — re-research everything + rewrite (~6 min)',
  manual_only: 'Manual edits only',
  none: 'No actionable feedback found',
};

const RECOMMENDATION_TOOLTIP: Record<FeedbackPlanRecommendation, string> = {
  v2_prompt_augmented:
    'The existing research is sufficient. Reviewer feedback will be injected into the LLM prompts and the executive summary will be rewritten — fast, no new document retrieval.',
  section_regen:
    'One or more sections have missing or outdated evidence. We will re-run the retrieval pipeline for just those sections, then rewrite the summary.',
  targeted_section_regen:
    'A small number of scored criteria are completely absent from the proposal. We will retrieve evidence for only those criteria (fast ~2 min path), then regenerate the full executive summary.',
  v1v2_full_regen:
    'More than 30% of sections are flagged or the proposal template has drifted significantly. A full research-and-rewrite pipeline is the most reliable fix (~6 min).',
  manual_only:
    'The reviewer added ready-made content that should be copy-pasted in directly. Use the editor on the Report Content tab — no AI call needed.',
  none: 'Nothing in the QA tab points to an actionable change right now.',
};

/** Plain-language CTA button label per recommendation. */
const RECOMMENDATION_CTA: Record<FeedbackPlanRecommendation, string> = {
  v2_prompt_augmented: 'Approve & rewrite with reviewer tips',
  section_regen: 'Approve & re-research + rewrite',
  targeted_section_regen: 'Approve & fill missing sections + rewrite',
  v1v2_full_regen: 'Approve & run full pipeline',
  manual_only: 'Nothing to regenerate — edit manually',
  none: 'Nothing to regenerate',
};

const REGEN_POLL_INTERVAL_MS = 3000;
// No hard cap — we poll until the job finishes or the component unmounts.
// Jobs can legitimately run for 30-60+ minutes; a cap of 240 × 2.5 s = 10 min
// was far too aggressive.

/** sessionStorage key for in-flight regen job, keyed per report. */
const regenStorageKey = (reportId: string) => `regen_job_v1_${reportId}`;

function coerceRegenerationJobToReportStatus(
  job: RegenerationJobStatus,
): ReportJobStatus {
  return job as unknown as ReportJobStatus;
}

function pctFromJob(j: ReportJobStatus | null): number | null {
  const raw = j?.progress_data?.percentage as unknown;
  if (typeof raw === 'number' && !Number.isNaN(raw)) {
    return Math.min(100, Math.max(0, raw));
  }
  const s = typeof raw === 'string' ? parseFloat(raw) : NaN;
  return !Number.isNaN(s) ? Math.min(100, Math.max(0, s)) : null;
}

function stageFromJob(j: ReportJobStatus | null): string | null {
  const pd = j?.progress_data as Record<string, unknown> | undefined | null;
  const fromPd =
    (typeof pd?.stage_message === 'string' && pd.stage_message) ||
    (typeof pd?.current_stage === 'string' && pd.current_stage) ||
    (typeof pd?.status_message === 'string' && pd.status_message) ||
    '';
  const fromTop =
    (typeof j?.message === 'string' && j.message) ||
    (typeof j?.stage === 'string' && j.stage) ||
    '';
  const combined = `${fromPd}`.trim() || `${fromTop}`.trim();
  return combined || null;
}

interface FeedbackPlanPreviewModalState {
  expanded: Set<string>;
  minSeverity: 'minor' | 'major' | 'critical';
  extraInstructions: string;
  includeGrammar: boolean;
}

export function FeedbackPlanPreviewModal({
  reportId,
  open,
  onClose,
  onPlanComputed,
  defaultMinSeverity = 'minor',
  autoComputeOnOpen = false,
  expandPromptPreviewOnOpen = false,
  embedJobProgressOverlay = true,
  onPlanApplied,
  onEmbeddedRegenerationComplete,
}: FeedbackPlanPreviewModalProps): React.ReactElement | null {
  const queryClient = useQueryClient();
  const { addToast } = useToastStore();
  const [state, setState] = useState<FeedbackPlanPreviewModalState>(() => ({
    expanded: new Set(),
    minSeverity: defaultMinSeverity,
    extraInstructions: '',
    includeGrammar: true,
  }));

  const [promptPreviewExpanded, setPromptPreviewExpanded] = useState(false);
  const prevOpenRef = useRef(false);
  const [trackedRegen, setTrackedRegen] = useState<{
    jobId: string;
    planId: string;
  } | null>(null);
  const [regenPollEnd, setRegenPollEnd] = useState<
    'running' | 'ok' | 'fail' | 'timeout'
  >('running');
  const [liveJob, setLiveJob] = useState<ReportJobStatus | null>(null);
  const [diffData, setDiffData] = useState<V2DiffResponse | null>(null);
  const [showDiff, setShowDiff] = useState(false);
  const [correctionsDirty, setCorrectionsDirty] = useState(false);

  const { data: reportData } = useQuery({
    queryKey: ['report', reportId],
    queryFn: () => getReport(reportId),
    enabled: open && !!reportId,
    staleTime: 60_000,
  });

  const rescoreMutation = useMutation<unknown, Error, void>({
    mutationFn: () => scoreReadiness({ reportId, forceRefresh: true }),
    onSuccess: () => {
      addToast({ type: 'success', title: 'Scored', message: 'Proposal re-scored — recomputing plan.' });
      // After scoring, always force a fresh plan (evaluation hash changed).
      planMutation.mutate({ forceRefresh: true });
    },
    onError: (err) => {
      addToast({ type: 'error', title: 'Score failed', message: err.message });
    },
  });

  const planMutation = useMutation<FeedbackPlanResponse, Error, { forceRefresh?: boolean }>({
    mutationFn: ({ forceRefresh } = {}) =>
      createFeedbackPlan(reportId, {
        min_severity: state.minSeverity,
        extra_instructions: state.extraInstructions.trim() || undefined,
        include_grammar_findings: state.includeGrammar,
        force_refresh: forceRefresh,
      }),
    onSuccess: (plan) => {
      onPlanComputed?.(plan);
    },
  });

  const applyMutation = useMutation<
    RegenerationJobStatus,
    FeedbackPlanApplyError,
    string
  >({
    mutationFn: (planId: string) =>
      applyFeedbackPlan(reportId, { feedback_plan_id: planId }),
    onSuccess: (job, planId) => {
      if (!planId) return;
      if (embedJobProgressOverlay) {
        setRegenPollEnd('running');
        setTrackedRegen({ jobId: job.id, planId });
        const seeded = coerceRegenerationJobToReportStatus(job);
        setLiveJob(seeded);
        // Persist so a page refresh or close/reopen can resume monitoring.
        try {
          sessionStorage.setItem(
            regenStorageKey(reportId),
            JSON.stringify({ jobId: job.id, planId }),
          );
        } catch { /* storage quota exceeded or private browsing — non-fatal */ }
      } else {
        onPlanApplied?.(job, planId);
      }
    },
  });

  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (!open) {
      // Don't wipe trackedRegen from sessionStorage — preserve it so
      // reopening the modal can resume the in-flight job.
      setTrackedRegen(null);
      setRegenPollEnd('running');
      setLiveJob(null);
      return;
    }
    if (!justOpened) return;

    // On reopen, check if there is an in-flight job stored for this report.
    // If it's still running, resume polling instead of starting from scratch.
    const stored = (() => {
      try {
        const raw = sessionStorage.getItem(regenStorageKey(reportId));
        return raw ? (JSON.parse(raw) as { jobId: string; planId: string }) : null;
      } catch {
        return null;
      }
    })();

    if (stored) {
      getReportJobStatus(stored.jobId)
        .then((st) => {
          if (['pending', 'queued', 'running', 'checkpointed'].includes(st.status)) {
            // Job still active — reattach the polling overlay.
            setRegenPollEnd('running');
            setTrackedRegen({ jobId: stored.jobId, planId: stored.planId });
            setLiveJob(st);
            return; // Skip applyMutation.reset() so Apply button stays locked.
          }
          // Job finished while modal was closed — clean up storage.
          try { sessionStorage.removeItem(regenStorageKey(reportId)); } catch { /* ok */ }
          applyMutation.reset();
          if (autoComputeOnOpen || expandPromptPreviewOnOpen) planMutation.mutate();
          setPromptPreviewExpanded(expandPromptPreviewOnOpen);
        })
        .catch(() => {
          // Status fetch failed — fall through to normal fresh-open path.
          try { sessionStorage.removeItem(regenStorageKey(reportId)); } catch { /* ok */ }
          applyMutation.reset();
          if (autoComputeOnOpen || expandPromptPreviewOnOpen) planMutation.mutate();
          setPromptPreviewExpanded(expandPromptPreviewOnOpen);
        });
      return; // The async branch above handles the rest.
    }

    applyMutation.reset();
    if (autoComputeOnOpen || expandPromptPreviewOnOpen) planMutation.mutate();
    setPromptPreviewExpanded(expandPromptPreviewOnOpen);
  }, [open, autoComputeOnOpen, expandPromptPreviewOnOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const finishEmbeddedRegenPolling = useCallback(async () => {
    try { sessionStorage.removeItem(regenStorageKey(reportId)); } catch { /* ok */ }
    addToast({
      type: 'success',
      title: 'V2 Regeneration Complete',
      message: 'Executive summary has been regenerated successfully.',
    });
    await queryClient.invalidateQueries({ queryKey: ['report', reportId] });
    await queryClient.invalidateQueries({ queryKey: ['report-v2-content', reportId] });
    await queryClient.invalidateQueries({ queryKey: ['report-v1-content', reportId] });
    await queryClient.invalidateQueries({ queryKey: ['report-staff-assignments', reportId] });
    await queryClient.refetchQueries({ queryKey: ['report', reportId] });
    // Fetch before/after diff for inline display.
    try {
      const diff = await getV2Diff(reportId);
      if (diff.has_previous) {
        setDiffData(diff);
      }
    } catch {
      // Diff is non-critical — swallow errors silently.
    }
    // Auto-rescore so QA tab reflects the new content immediately.
    rescoreMutation.mutate();
    await Promise.resolve(onEmbeddedRegenerationComplete?.());
  }, [addToast, onEmbeddedRegenerationComplete, queryClient, reportId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stable refs so the polling effect never needs to include these callbacks as deps.
  // Using refs prevents the effect from restarting every time a toast fires (which
  // would cause an infinite loop: addToast → Zustand setState → re-render → new
  // addToast reference → effect re-runs → addToast fires again → …).
  const addToastRef = useRef(addToast);
  addToastRef.current = addToast;
  const finishPollingRef = useRef(finishEmbeddedRegenPolling);
  finishPollingRef.current = finishEmbeddedRegenPolling;

  useEffect(() => {
    if (!trackedRegen || !embedJobProgressOverlay) return;
    const regenJobId = trackedRegen.jobId;

    let stopped = false;
    // Defer the kick-off toast so it doesn't fire inside React's commit phase.
    setTimeout(() => {
      if (!stopped) {
        addToastRef.current({
          type: 'info',
          title: 'V2 Regeneration (QA feedback)',
          message: `Job ${regenJobId.slice(0, 8)}… is running.`,
        });
      }
    }, 0);

    async function pollLoop() {
      // Poll indefinitely — no hard cap. Long-running jobs (30-60+ min) are
      // normal for full pipeline regenerations. The loop stops only when the
      // job reaches a terminal state or the component unmounts (`stopped = true`).
      while (!stopped) {
        try {
          const st = await getReportJobStatus(regenJobId);
          if (stopped) return;
          setLiveJob(st);
          if (st.status === 'completed') {
            await finishPollingRef.current();
            if (!stopped) setRegenPollEnd('ok');
            return;
          }
          if (st.status === 'failed') {
            if (!stopped) {
              try { sessionStorage.removeItem(regenStorageKey(reportId)); } catch { /* ok */ }
              addToastRef.current({
                type: 'error',
                title: 'V2 Regeneration Failed',
                message: st.error_message ?? 'Unknown error',
              });
              setRegenPollEnd('fail');
            }
            return;
          }
        } catch {
          /* network blip — keep polling */
        }
        if (!stopped) {
          await new Promise((r) => setTimeout(r, REGEN_POLL_INTERVAL_MS));
        }
      }
    }

    void pollLoop();
    return () => {
      stopped = true;
    };
  }, [trackedRegen, embedJobProgressOverlay]); // stable: callbacks accessed via refs above

  const plan = planMutation.data;
  const staffRoleTitles = React.useMemo(() => {
    const titles: Record<string, string> = {};
    const templateResult = reportData?.meta?.template_result as
      | { staff_requirements?: Array<{ position_id?: string; role_title?: string }> }
      | undefined;
    for (const req of templateResult?.staff_requirements ?? []) {
      if (req.position_id && req.role_title) {
        titles[String(req.position_id)] = String(req.role_title);
      }
    }
    const fromPlan = plan?.payload?.buckets?.section_regen?.staff_role_titles ?? {};
    return { ...titles, ...fromPlan };
  }, [plan, reportData?.meta?.template_result]);

  if (!open) {
    return null;
  }

  const applyRecommendation: FeedbackPlanRecommendation = plan
    ? effectiveFeedbackPlanRecommendation(plan)
    : 'none';
  const v2PromptWork = plan ? feedbackPlanHasV2PromptWork(plan.payload) : false;
  const applyError = applyMutation.error;
  const applyAlreadyApplied = applyError?.code === 'feedback_plan_already_applied';
  const applyRescoreFailed = applyError?.code === 'evaluation_rescore_failed';

  const toggleExpanded = (key: string) => {
    setState((prev) => {
      const next = new Set(prev.expanded);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return { ...prev, expanded: next };
    });
  };

  const dismissRegenOverlay = () => {
    setTrackedRegen(null);
    setRegenPollEnd('running');
    setLiveJob(null);
    applyMutation.reset();
  };

  const regenPct = pctFromJob(liveJob);
  const regenStage = stageFromJob(liveJob);
  const closesBlocked =
    !!trackedRegen &&
    embedJobProgressOverlay &&
    regenPollEnd === 'running';

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-label="Feedback plan preview"
      onClick={() => {
        if (!closesBlocked) onClose();
      }}
    >
      <div
        className={`relative max-h-[92vh] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl transition-[width] duration-300 ${showDiff && diffData?.has_previous && regenPollEnd === 'ok' ? 'w-[min(1400px,97vw)]' : 'w-[min(900px,95vw)]'}`}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {embedJobProgressOverlay && trackedRegen ? (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
            aria-live="polite"
            aria-label="V2 regeneration progress"
          >
            <div className={`rounded-xl border border-slate-200 bg-white p-6 shadow-2xl ${showDiff && diffData?.has_previous ? 'w-[min(1400px,95vw)]' : 'w-[min(560px,95vw)]'}`}>
              <div className="text-lg font-semibold text-slate-900">
                Regenerating executive summary (V2)
              </div>
              <p className="mt-1 text-xs text-slate-500 font-mono break-all">
                Job {trackedRegen.jobId}
              </p>

              {regenPollEnd === 'running' ? (
                <div className="mt-4 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Progress</span>
                    <span className="font-medium text-indigo-800">
                      {regenPct != null ? `${Math.round(regenPct)}%` : 'Starting…'}
                    </span>
                  </div>
                  <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-200">
                    <div
                      className="h-2.5 rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-[width] duration-500"
                      style={{
                        width:
                          regenPct != null
                            ? `${Math.min(100, Math.max(4, regenPct))}%`
                            : '8%',
                      }}
                      role="progressbar"
                      aria-valuenow={
                        regenPct != null ? Math.round(regenPct) : undefined
                      }
                      aria-valuemin={0}
                      aria-valuemax={100}
                    />
                  </div>
                  <div className="min-h-[2.75rem] text-sm text-slate-700">
                    {regenStage ? (
                      <span>{regenStage}</span>
                    ) : (
                      <span className="text-slate-500 italic">
                        Waiting for worker status&hellip;
                      </span>
                    )}
                  </div>
                  {/* No raw JSON dump — progress is shown via the stage label above. */}
                  <div className="flex items-center gap-2 pt-2 text-xs text-slate-500">
                    <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                    Updating every few seconds — same telemetry as proposal
                    generation.
                  </div>
                </div>
              ) : null}

              {regenPollEnd === 'ok' ? (
                <div className="mt-4 space-y-4">
                  <div className="flex items-start gap-2 rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                    <CheckCircle className="mt-0.5 h-5 w-5 shrink-0" />
                    <div>
                      Executive summary regenerated. Fresh content is synced to
                      this report.
                    </div>
                  </div>

                  {/* Before / After diff panel */}
                  {diffData?.has_previous ? (
                    <div className="rounded border border-slate-200 bg-slate-50">
                      <button
                        type="button"
                        className="flex w-full items-center justify-between px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                        onClick={() => setShowDiff((v) => !v)}
                      >
                        <span className="flex items-center gap-1.5">
                          <Columns2 className="h-3.5 w-3.5" />
                          View before / after diff
                        </span>
                        {showDiff ? (
                          <ChevronDown className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5" />
                        )}
                      </button>
                      {showDiff && (
                        <V2DiffPanel
                          previous={diffData.previous ?? ''}
                          current={diffData.current ?? ''}
                        />
                      )}
                    </div>
                  ) : null}

                  <Button
                    type="button"
                    className="w-full"
                    onClick={() => {
                      dismissRegenOverlay();
                      onClose();
                    }}
                  >
                    Done
                  </Button>
                </div>
              ) : null}

              {regenPollEnd === 'fail' ? (
                <div className="mt-4 space-y-4">
                  <div className="flex items-start gap-2 rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                    <div>
                      Job failed — check worker logs or try again after fixing
                      the error below.
                      {liveJob?.error_message ? (
                        <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap font-mono text-xs">
                          {liveJob.error_message}
                        </pre>
                      ) : null}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={dismissRegenOverlay}
                  >
                    Back to plan
                  </Button>
                </div>
              ) : null}

              {/* 'timeout' state is no longer reachable — polling runs until the
                  job reaches a terminal state or the component unmounts. */}
            </div>
          </div>
        ) : null}

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5">
          <div className="space-y-1">
            <div className="text-lg font-semibold text-slate-900">
              Feedback → Regeneration plan
            </div>
            <div className="text-sm text-slate-600">
              Buckets gaps, QA comments, and revision requests before any LLM call.
              Use <strong className="text-slate-800">Prompt preview</strong> below to
              read what ships into V2 regeneration, then approve.
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={closesBlocked ? undefined : onClose}
            disabled={closesBlocked}
            aria-label="Close"
          >
            <XIcon className="h-4 w-4" />
          </Button>
        </div>

        <div className="space-y-4 p-5">
          <FeedbackPlanModalStepsGuide />

          <SelectionCorrectionsPanel
            reportId={reportId}
            onDirtyChange={setCorrectionsDirty}
            onSaved={() => {
              setCorrectionsDirty(false);
              planMutation.mutate({ forceRefresh: true });
            }}
            onOpenRegenPlan={() => {
              if (correctionsDirty) return;
              planMutation.mutate({ forceRefresh: true });
            }}
          />

          {correctionsDirty && (
            <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Save selection corrections before recomputing the plan — unsaved changes are not included.
            </div>
          )}

          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col text-xs font-medium text-slate-700">
              Min severity
              <select
                value={state.minSeverity}
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    minSeverity: e.target.value as
                      | 'minor'
                      | 'major'
                      | 'critical',
                  }))
                }
                className="mt-1 rounded border border-slate-300 bg-white px-2 py-1 text-sm"
              >
                <option value="minor">minor</option>
                <option value="major">major</option>
                <option value="critical">critical</option>
              </select>
            </label>
            <label className="flex-1 min-w-[260px] flex flex-col text-xs font-medium text-slate-700">
              Extra instructions (threaded as global guidance)
              <input
                type="text"
                placeholder="e.g. Tighten the company section; cap each project at 8 lines."
                value={state.extraInstructions}
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    extraInstructions: e.target.value,
                  }))
                }
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    planMutation.mutate({ forceRefresh: true });
                  }
                }}
                className="mt-1 rounded border border-slate-300 bg-white px-2 py-1 text-sm"
                maxLength={2000}
              />
            </label>
            <label className="flex items-center gap-1.5 text-xs font-medium text-slate-700 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={state.includeGrammar}
                onChange={(e) =>
                  setState((prev) => ({ ...prev, includeGrammar: e.target.checked }))
                }
                className="rounded border-slate-300"
              />
              Include grammar / formatting checks
            </label>
            <Button
              type="button"
              size="sm"
              onClick={() => {
                if (correctionsDirty) {
                  addToast({
                    type: 'warning',
                    title: 'Unsaved corrections',
                    message: 'Save selection corrections before recomputing the plan.',
                  });
                  return;
                }
                planMutation.mutate({ forceRefresh: !!plan });
              }}
              disabled={planMutation.isPending}
            >
              {planMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Computing&hellip;
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  {plan ? 'Recompute plan' : 'Compute plan'}
                </>
              )}
            </Button>
          </div>

          {planMutation.isError && (
            <div className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div className="flex-1">
                <div className="font-medium">Could not compute feedback plan</div>
                <div className="text-xs mt-0.5">
                  {String((planMutation.error as Error)?.message ?? 'Unknown error')}
                </div>
              </div>
            </div>
          )}

          {planMutation.isPending && !plan && (
            <div className="flex items-center gap-2 rounded border border-indigo-200 bg-indigo-50/80 p-3 text-sm text-indigo-900">
              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
              Computing feedback plan — bucketing QA signals&hellip;
            </div>
          )}

          {!plan &&
            !planMutation.isPending &&
            !planMutation.isError &&
            !autoComputeOnOpen &&
            !expandPromptPreviewOnOpen && (
            <div className="flex items-center gap-2 rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
              <Info className="h-4 w-4" />
              <span>
                Click <strong>&nbsp;Compute plan&nbsp;</strong> to bucket
                evaluation gaps, reviewer comments, and revision requests.
              </span>
            </div>
          )}

          {plan && (
            <div className="space-y-4">
              <RecommendationBanner
                recommendation={applyRecommendation}
                summary={plan.payload.summary}
              />

              <div className="overflow-hidden rounded-lg border border-indigo-200 bg-indigo-50/40">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm font-semibold text-indigo-950 hover:bg-indigo-50"
                  onClick={() => setPromptPreviewExpanded((prev) => !prev)}
                  aria-expanded={promptPreviewExpanded}
                >
                  {promptPreviewExpanded ? (
                    <ChevronDown className="h-4 w-4 shrink-0" />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0" />
                  )}
                  Prompt preview — text threaded into V2 regeneration
                </button>
                {promptPreviewExpanded && (
                  <pre className="max-h-52 overflow-auto border-t border-indigo-200 bg-white p-3 text-xs leading-relaxed text-slate-800 whitespace-pre-wrap font-mono dark:bg-slate-900 dark:text-slate-100">
                    {buildFeedbackPlanPromptPreviewText(plan.payload)}
                  </pre>
                )}
              </div>

              <BucketCard
                title="A — Rewrite with reviewer tips (V2 only, fastest)"
                subtitle="The existing research is good enough; we'll inject these QA notes into the LLM prompts and rewrite the executive summary."
                count={plan.payload.summary.v2_target_count}
                icon="A"
              >
                {!v2PromptWork && (
                  <EmptyHint text="No prose-level fixes to thread into the V2 prompt." />
                )}
                {sortFeedbackTargetEntries(
                  Object.entries(
                    plan.payload.buckets.v2_prompt_augmented.feedback_by_target,
                  ),
                ).map(([targetKey, items]) => (
                  <div
                    key={targetKey}
                    className="rounded border border-slate-200 bg-slate-50 p-2"
                  >
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 text-left text-sm font-medium text-slate-800"
                      onClick={() => toggleExpanded(`v2:${targetKey}`)}
                    >
                      {state.expanded.has(`v2:${targetKey}`) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      <span className="font-mono text-xs uppercase tracking-wide text-slate-500">
                        {targetKey.split(':')[0]}
                      </span>
                      <span>
                        {formatFeedbackTargetLabel(targetKey, staffRoleTitles)}
                      </span>
                      {items.some((it) =>
                        String(it.source_id ?? '').startsWith('correction:'),
                      ) && (
                        <Badge variant="warning" className="text-[10px]">
                          correction
                        </Badge>
                      )}
                      <Badge variant="info" className="ml-auto">
                        {items.length} item{items.length === 1 ? '' : 's'}
                      </Badge>
                    </button>
                    {state.expanded.has(`v2:${targetKey}`) && (
                      <ul className="mt-2 space-y-1 pl-6 text-sm text-slate-700">
                        {[...items]
                          .sort((a, b) => {
                            const aCorr = String(a.source_id ?? '').startsWith('correction:');
                            const bCorr = String(b.source_id ?? '').startsWith('correction:');
                            if (aCorr && !bCorr) return -1;
                            if (!aCorr && bCorr) return 1;
                            return 0;
                          })
                          .map((it, i) => (
                          <li
                            key={`${targetKey}-${i}`}
                            className="list-disc text-xs"
                          >
                            {it.text}
                            {it.source_id && (
                              <span className="ml-1 text-[10px] text-slate-400">
                                ({it.source_id})
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
                {plan.payload.buckets.v2_prompt_augmented.global_instructions && (
                  <div className="rounded border border-slate-200 bg-white p-2 text-xs text-slate-700">
                    <div className="mb-1 font-medium text-slate-800">
                      Global instructions
                    </div>
                    <pre className="whitespace-pre-wrap font-sans">
                      {plan.payload.buckets.v2_prompt_augmented.global_instructions}
                    </pre>
                  </div>
                )}
              </BucketCard>

              <BucketCard
                title="B1 — Re-research specific sections (new retrieval)"
                subtitle="Some sections have weak or missing evidence. We'll pull fresh documents from the knowledge base for just these sections, then rewrite."
                count={Math.max(
                  plan.payload.summary.section_regen_count,
                  plan.payload.summary.staff_research_count ?? 0,
                  (plan.payload.buckets.section_regen.staff_research ?? []).length,
                )}
                icon="B1"
              >
                {(plan.payload.buckets.section_regen.staff_research ?? []).map(
                  (target) => (
                    <div
                      key={target.position_id}
                      className="mb-2 rounded border border-indigo-200 bg-indigo-50/60 p-2 text-xs text-slate-800"
                    >
                      <p className="font-semibold text-slate-900">
                        Staff re-research:{' '}
                        {target.role_title || staffRoleTitles[target.position_id] || target.position_id}
                      </p>
                      {target.reason?.trim() && (
                        <p className="mt-1">
                          <span className="font-medium">Why:</span> {target.reason}
                        </p>
                      )}
                      {(target.queries ?? []).map((q, i) => (
                        <p key={`${target.position_id}-q-${i}`} className="mt-1">
                          <span className="font-medium">Search:</span> {q}
                        </p>
                      ))}
                    </div>
                  ),
                )}
                {plan.payload.buckets.section_regen.section_ids.length === 0 &&
                  (plan.payload.buckets.section_regen.staff_research ?? []).length === 0 && (
                  <EmptyHint text="No sections need a fresh RAG pass." />
                )}
                {plan.payload.buckets.section_regen.section_ids.length > 0 && (
                  <ul className="space-y-1 pl-2 text-sm text-slate-700">
                    {plan.payload.buckets.section_regen.section_ids.map(
                      (sid) => (
                        <li
                          key={sid}
                          className="list-disc text-xs font-mono"
                        >
                          Section {sid}
                        </li>
                      ),
                    )}
                  </ul>
                )}
              </BucketCard>

              <BucketCard
                title={
                  plan.payload.summary.targeted_regen_triggered
                    ? 'B2 — Fill missing scored sections + rewrite (~2 min)'
                    : 'B2 — Full research pipeline + rewrite (~6 min)'
                }
                subtitle={
                  plan.payload.summary.targeted_regen_triggered
                    ? `${plan.payload.summary.targeted_criterion_count ?? 1} scored criteria are missing from the proposal. We will research only those criteria, then regenerate the executive summary.`
                    : 'More than 30% of sections are flagged or the proposal template has changed significantly. The full research pipeline is the most reliable fix.'
                }
                count={plan.payload.summary.full_regen_triggered ? 1 : 0}
                icon="B2"
              >
                {!plan.payload.summary.full_regen_triggered && (
                  <EmptyHint text="Not needed — the existing research covers all scored criteria." />
                )}
                {plan.payload.buckets.v1v2_full_regen.trigger_reasons.length > 0 && (
                  <ul className="space-y-1 pl-2 text-sm text-slate-700">
                    {plan.payload.buckets.v1v2_full_regen.trigger_reasons.map(
                      (r, i) => (
                        <li key={i} className="list-disc text-xs">
                          {r}
                        </li>
                      ),
                    )}
                  </ul>
                )}
                {plan.payload.buckets.v1v2_full_regen.targeted_criterion_ids &&
                  plan.payload.buckets.v1v2_full_regen.targeted_criterion_ids.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {plan.payload.buckets.v1v2_full_regen.targeted_criterion_ids.map((cid) => (
                        <span key={cid} className="inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-900">
                          {cid}
                        </span>
                      ))}
                    </div>
                  )}
                {plan.payload.summary.full_regen_triggered && (
                  <div className="mt-1 text-xs text-slate-500">
                    Estimated runtime:{' '}
                    {plan.payload.buckets.v1v2_full_regen.estimated_runtime_minutes} min
                  </div>
                )}
              </BucketCard>

              <BucketCard
                title="C — Manual edits only (use the editor)"
                subtitle="These items contain ready-made content from the reviewer that should be copy-pasted in directly. No AI call needed."
                count={plan.payload.summary.manual_count}
                icon="C"
              >
                {plan.payload.buckets.manual_only.length === 0 && (
                  <EmptyHint text="No manual-only items." />
                )}
                {plan.payload.buckets.manual_only.length > 0 && (
                  <ul className="space-y-1 pl-2 text-sm text-slate-700">
                    {plan.payload.buckets.manual_only.map((m) => (
                      <li
                        key={m.comment_id}
                        className="list-disc text-xs"
                      >
                        <span className="font-mono">{m.comment_id}</span> -{' '}
                        {m.reason}
                      </li>
                    ))}
                  </ul>
                )}
              </BucketCard>
            </div>
          )}
        </div>

        {applyError && (
          <div
            className="mx-5 mb-3 flex items-start gap-2 rounded border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900"
            role="alert"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-medium">
                {applyAlreadyApplied
                  ? 'This plan was already applied'
                  : applyRescoreFailed
                  ? 'Could not refresh the technical score automatically'
                  : 'Could not apply feedback plan'}
              </div>
              <div className="text-xs mt-0.5">
                {applyAlreadyApplied
                  ? 'A regeneration job already consumed this plan. Compute a fresh plan from the latest evaluation to apply more feedback.'
                  : applyRescoreFailed
                  ? 'Use Re-score from the QA panel, then recompute and apply the plan.'
                  : applyError.message}
              </div>
              {applyRescoreFailed && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="mt-2 border-rose-300 text-rose-900 hover:bg-rose-100"
                  onClick={() => rescoreMutation.mutate()}
                  disabled={rescoreMutation.isPending}
                >
                  {rescoreMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                      Scoring…
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-3.5 w-3.5" />
                      Re-score &amp; recompute plan
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        )}

        {applyMutation.isSuccess && applyMutation.data && !embedJobProgressOverlay && (
          <div
            className="mx-5 mb-3 flex items-start gap-2 rounded border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900"
            role="status"
          >
            <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div>
              <div className="font-medium">Regeneration started</div>
              <div className="text-xs">
                Job <span className="font-mono">{applyMutation.data.id}</span>{' '}
                queued. Track progress in the V2 progress panel.
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 p-4">
          <span className="mr-auto text-xs text-slate-500">
            {plan ? `Plan id: ${plan.plan_id}` : ''}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={closesBlocked}
          >
            Close
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={
              !!trackedRegen ||
              !plan ||
              applyMutation.isPending ||
              applyMutation.isSuccess ||
              applyRecommendation === 'manual_only' ||
              applyRecommendation === 'none'
            }
            onClick={() => {
              if (plan) {
                applyMutation.mutate(plan.plan_id);
              }
            }}
            title={
              plan
                ? applyRecommendation === 'manual_only' ||
                  applyRecommendation === 'none'
                  ? 'Nothing to regenerate from this plan — make manual edits or recompute.'
                  : RECOMMENDATION_TOOLTIP[applyRecommendation]
                : 'Compute a plan first.'
            }
          >
            {applyMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Queuing&hellip;
              </>
            ) : trackedRegen && regenPollEnd === 'running' ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Running&hellip;
              </>
            ) : trackedRegen ? (
              <>
                <CheckCircle className="mr-2 h-4 w-4 text-emerald-600" />
                {regenPollEnd === 'ok'
                  ? 'Complete — dismiss overlay above'
                  : regenPollEnd === 'fail'
                    ? 'Failed — see overlay'
                    : 'Check overlay for status'}
              </>
            ) : applyMutation.isSuccess ? (
              <>
                <CheckCircle className="mr-2 h-4 w-4" />
                Applied
              </>
            ) : plan ? (
              <>
                {applyRecommendation === 'targeted_section_regen' && (
                  <Zap className="mr-2 h-4 w-4" />
                )}
                {RECOMMENDATION_CTA[applyRecommendation] ?? 'Approve & regenerate V2'}
              </>
            ) : (
              'Approve & regenerate V2'
            )}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function FeedbackPlanModalStepsGuide(): React.ReactElement {
  return (
    <div
      role="navigation"
      aria-label="Feedback plan workflow steps"
      className="rounded-lg border border-indigo-400/70 bg-gradient-to-br from-indigo-50 via-violet-50 to-sky-50 p-4 shadow-sm ring-1 ring-indigo-200/60"
    >
      <div className="mb-3 text-sm font-semibold text-indigo-950">
        How to use this flow
      </div>
      <ol className="m-0 list-none space-y-3 p-0 text-sm text-indigo-950/90">
        <li className="flex gap-3">
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-xs font-bold text-white shadow-sm"
            aria-hidden
          >
            1
          </span>
          <span>
            <strong className="text-indigo-950">Compute &amp; bucket.</strong>{' '}
            Gaps, QA comments, and revision requests are grouped into surgical V2,
            RAG rerun, full regen, or manual edits. Opening from the score card
            runs this automatically (use <strong>Recompute plan</strong> after
            changing severity or instructions).
          </span>
        </li>
        <li className="flex gap-3">
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-600 text-xs font-bold text-white shadow-sm"
            aria-hidden
          >
            2
          </span>
          <span>
            <strong className="text-indigo-950">Review prompt preview.</strong>{' '}
            Expand the accordion to read verbatim what threads into V2 prompts.
          </span>
        </li>
        <li className="flex gap-3">
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-600 text-xs font-bold text-white shadow-sm"
            aria-hidden
          >
            3
          </span>
          <span>
            <strong className="text-indigo-950">Approve &amp; watch progress.</strong>{' '}
            Confirmation queues the job; stay here for percentage, stage, and
            streamed worker details — same telemetry as proposal generation.
          </span>
        </li>
      </ol>
    </div>
  );
}

function BucketCard({
  title,
  subtitle,
  count,
  icon,
  children,
}: {
  title: string;
  subtitle?: string;
  count: number;
  icon: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded border border-slate-200 bg-white p-3">
      <div className="mb-1 flex items-start gap-2">
        <span className="mt-0.5 inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded bg-slate-200 px-2 text-xs font-bold text-slate-700 shrink-0">
          {icon}
        </span>
        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-800">{title}</div>
          {subtitle && (
            <div className="mt-0.5 text-xs text-slate-500 leading-relaxed">{subtitle}</div>
          )}
        </div>
        <Badge variant={count > 0 ? 'info' : 'default'} className="ml-auto shrink-0">
          {count}
        </Badge>
      </div>
      <div className="mt-2 space-y-2">{children}</div>
    </div>
  );
}

function EmptyHint({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2 text-xs text-slate-500">
      <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
      {text}
    </div>
  );
}

function RecommendationBanner({
  recommendation,
  summary,
}: {
  recommendation: FeedbackPlanRecommendation;
  summary: FeedbackPlanResponse['payload']['summary'];
}) {
  const isTargeted = recommendation === 'targeted_section_regen';
  const isFull = recommendation === 'v1v2_full_regen';
  const Icon = recommendation === 'none' ? Info : isTargeted ? Zap : RefreshCw;
  const bannerClass = isTargeted
    ? 'rounded border border-amber-200 bg-amber-50 p-3 text-sm'
    : isFull
    ? 'rounded border border-rose-200 bg-rose-50 p-3 text-sm'
    : 'rounded border border-blue-200 bg-blue-50 p-3 text-sm';
  const headingClass = isTargeted
    ? 'flex items-center gap-2 font-semibold text-amber-900'
    : isFull
    ? 'flex items-center gap-2 font-semibold text-rose-900'
    : 'flex items-center gap-2 font-semibold text-blue-900';
  const bodyClass = isTargeted ? 'mt-1 text-xs text-amber-800' : isFull ? 'mt-1 text-xs text-rose-800' : 'mt-1 text-xs text-blue-800';
  const statsClass = isTargeted ? 'mt-2 grid grid-cols-2 gap-2 text-xs text-amber-900 md:grid-cols-4' : isFull ? 'mt-2 grid grid-cols-2 gap-2 text-xs text-rose-900 md:grid-cols-4' : 'mt-2 grid grid-cols-2 gap-2 text-xs text-blue-900 md:grid-cols-4';

  return (
    <div className={bannerClass}>
      <div className={headingClass}>
        <Icon className="h-4 w-4" />
        Recommended: {RECOMMENDATION_LABEL[recommendation]}
      </div>
      <div className={bodyClass}>
        {RECOMMENDATION_TOOLTIP[recommendation]}
      </div>
      <div className={statsClass}>
        <div>
          <span className="font-medium">Reviewer tips:</span>{' '}
          {summary.v2_target_count}
        </div>
        <div>
          <span className="font-medium">Re-research:</span>{' '}
          {summary.section_regen_count}
          {(summary.staff_research_count ?? 0) > 0 && (
            <span className="ml-1">
              (+{summary.staff_research_count} staff role
              {(summary.staff_research_count ?? 0) === 1 ? '' : 's'})
            </span>
          )}
        </div>
        {(summary.staff_research_triggered ?? false) && (
          <div className="md:col-span-2">
            <span className="font-medium">Staff re-research:</span>{' '}
            {summary.staff_research_count ?? 0} role(s) — matcher will re-run with your search hints before V2 rewrite.
          </div>
        )}
        <div>
          <span className="font-medium">
            {summary.targeted_regen_triggered ? 'Missing criteria:' : 'Full pipeline:'}
          </span>{' '}
          {summary.targeted_regen_triggered
            ? summary.targeted_criterion_count ?? 1
            : summary.full_regen_triggered
            ? 'yes'
            : 'no'}
        </div>
        <div>
          <span className="font-medium">Manual:</span> {summary.manual_count}
        </div>
        {(summary.grammar_findings_count ?? 0) > 0 && (
          <div>
            <span className="font-medium">Grammar/format:</span>{' '}
            {summary.grammar_findings_count}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// V2 before/after diff panel — pure client-side line diff, no extra deps.
// ---------------------------------------------------------------------------

/**
 * Splits `a` into lines, splits `b` into lines, and returns a unified diff
 * where each entry is tagged `added`, `removed`, or `unchanged`.
 *
 * This is a simple O(n²) LCS-based diff sufficient for document-size text.
 * It avoids any external dependency by using a greedy Myers-style approach.
 */
function computeLineDiff(
  a: string,
  b: string,
): { tag: 'added' | 'removed' | 'unchanged'; text: string }[] {
  const aLines = a.split('\n');
  const bLines = b.split('\n');
  const result: { tag: 'added' | 'removed' | 'unchanged'; text: string }[] =
    [];

  // Build LCS table
  const m = aLines.length;
  const n = bLines.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () =>
    new Array<number>(n + 1).fill(0),
  );
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      if (aLines[i] === bLines[j]) {
        dp[i][j] = 1 + dp[i + 1][j + 1];
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }

  let i = 0;
  let j = 0;
  while (i < m || j < n) {
    if (i < m && j < n && aLines[i] === bLines[j]) {
      result.push({ tag: 'unchanged', text: aLines[i] });
      i++;
      j++;
    } else if (j < n && (i >= m || dp[i + 1][j] <= dp[i][j + 1])) {
      result.push({ tag: 'added', text: bLines[j] });
      j++;
    } else {
      result.push({ tag: 'removed', text: aLines[i] });
      i++;
    }
  }
  return result;
}

/** Convert a unified diff into side-by-side row pairs.
 * Consecutive removed + added blocks are zipped together so they sit
 * across from each other. Unchanged lines appear in both columns.
 */
type SideBySideRow =
  | { kind: 'unchanged'; text: string }
  | { kind: 'pair'; left: string | null; right: string | null };

function toSideBySide(
  lines: { tag: 'added' | 'removed' | 'unchanged'; text: string }[],
): SideBySideRow[] {
  const rows: SideBySideRow[] = [];
  let i = 0;
  while (i < lines.length) {
    if (lines[i].tag === 'unchanged') {
      rows.push({ kind: 'unchanged', text: lines[i].text });
      i++;
    } else {
      const removed: string[] = [];
      const added: string[] = [];
      while (i < lines.length && lines[i].tag === 'removed') {
        removed.push(lines[i].text);
        i++;
      }
      while (i < lines.length && lines[i].tag === 'added') {
        added.push(lines[i].text);
        i++;
      }
      const maxLen = Math.max(removed.length, added.length);
      for (let k = 0; k < maxLen; k++) {
        rows.push({
          kind: 'pair',
          left: removed[k] ?? null,
          right: added[k] ?? null,
        });
      }
    }
  }
  return rows;
}

function V2DiffPanel({
  previous,
  current,
}: {
  previous: string;
  current: string;
}): React.ReactElement {
  const lines = computeLineDiff(previous, current);
  const rows = toSideBySide(lines);

  const addedCount = lines.filter((l) => l.tag === 'added').length;
  const removedCount = lines.filter((l) => l.tag === 'removed').length;

  return (
    <div className="border-t border-slate-200">
      {/* Stats bar */}
      <div className="flex items-center gap-4 border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-500">
        <span className="text-rose-600 font-medium">−{removedCount} removed</span>
        <span className="text-emerald-600 font-medium">+{addedCount} added</span>
      </div>
      {/* Side-by-side headers */}
      <div className="grid grid-cols-2 divide-x divide-slate-200 border-b border-slate-200 bg-slate-100 text-xs font-semibold text-slate-500">
        <div className="px-3 py-1.5">Before</div>
        <div className="px-3 py-1.5">After</div>
      </div>
      {/* Diff rows */}
      <div className="max-h-[60vh] overflow-auto bg-white font-mono text-xs leading-5">
        {rows.map((row, idx) => {
          if (row.kind === 'unchanged') {
            if (row.text.trim() === '') return <div key={idx} className="grid grid-cols-2 divide-x divide-slate-100 h-3" />;
            return (
              <div key={idx} className="grid grid-cols-2 divide-x divide-slate-100">
                <div className="px-3 py-0.5 text-slate-500 whitespace-pre-wrap break-all">{row.text || '\u00a0'}</div>
                <div className="px-3 py-0.5 text-slate-500 whitespace-pre-wrap break-all">{row.text || '\u00a0'}</div>
              </div>
            );
          }
          // changed pair
          return (
            <div key={idx} className="grid grid-cols-2 divide-x divide-slate-200">
              <div className={`px-3 py-0.5 whitespace-pre-wrap break-all ${row.left != null ? 'bg-rose-50 text-rose-900 line-through opacity-75' : 'bg-slate-50'}`}>
                {row.left != null ? (row.left || '\u00a0') : ''}
              </div>
              <div className={`px-3 py-0.5 whitespace-pre-wrap break-all ${row.right != null ? 'bg-emerald-50 text-emerald-900' : 'bg-slate-50'}`}>
                {row.right != null ? (row.right || '\u00a0') : ''}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
